import { t } from 'i18next';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  useColorMode
} from '@chakra-ui/react';
import Exit from '../../icons/Exit';
import NotiOff from '../../icons/NotiOff';
import { Bell, Edit, LikeLine } from '@peacecover/icon';
import { useSuccessPhotoCardToast } from '../../presentation/template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { useUpdateUserPreferences } from '../../domains/user/ui/hooks/useUpdateUserPreferences';

interface BettieChatRoomSettingBottomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isBettieChatAlarm: boolean;
  leavePopupOpen: () => void;
  openNicknameUpdateSheet: () => void;
  openProfileUpdateSheet: () => void;
}

const BettieChatRoomSettingBottomDrawer = ({
  isOpen,
  onClose,
  isBettieChatAlarm,
  leavePopupOpen,
  openNicknameUpdateSheet,
  openProfileUpdateSheet
}: BettieChatRoomSettingBottomDrawerProps) => {
  const { colorMode } = useColorMode();
  const successPhotoCardToast = useSuccessPhotoCardToast();
  const { mutateAsync: updateUserPreferences } = useUpdateUserPreferences();

  const handleAlarm = async () => {
    try {
      const response = await updateUserPreferences({
        preferences: {
          alert: {
            bettie_chat: !isBettieChatAlarm
          }
        }
      });

      if (response.ok) {
        successPhotoCardToast(
          'bottom',
          isBettieChatAlarm
            ? (t('BettieChatRoom.turnOffAlarm') as string)
            : (t('BettieChatRoom.turnOnAlarm') as string)
        );
      } else {
        throw new Error('Failed to update user preferences');
      }
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
    }
  };

  const handleLeaveChatRoom = async () => {
    onClose();
    leavePopupOpen();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerOverlay />
      <DrawerContent
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
        style={{
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          paddingBottom: '30px',
          boxShadow:
            colorMode === 'dark'
              ? '0 -6px 7.5px 0 rgba(0, 0, 0, 0.65)'
              : '0 -5px 8.8px 0 rgba(0, 0, 0, 0.07)'
        }}
      >
        <DrawerHeader>
          <div className="flex justify-center">
            <div className="w-[38px] h-[4px] bg-[#D9D9D9] rounded-[2px] mb-[4px]" />
          </div>
        </DrawerHeader>
        <DrawerBody p={0}>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={openProfileUpdateSheet}
              className="flex items-center px-[24px] py-[16px] gap-[12px]"
            >
              <LikeLine className="text-text-main-lv2" size={20} />
              <p className="text-[14px] font-medium leading-[17px] text-text-main-lv1">
                {t('BettieChatRoom.profileUpdateDrawer.title')}
              </p>
            </button>
            <button
              type="button"
              onClick={openNicknameUpdateSheet}
              className="flex items-center px-[24px] py-[16px] gap-[12px]"
            >
              <Edit className="text-text-main-lv2" size={20} />
              <p className="text-[14px] font-medium leading-[17px] text-text-main-lv1">
                {t('BettieChatRoom.updateMyNickname')}
              </p>
            </button>
            <button
              type="button"
              onClick={handleAlarm}
              className="flex items-center px-[24px] py-[16px] gap-[12px]"
            >
              {isBettieChatAlarm ? (
                <NotiOff fill={colorMode === 'dark' ? '#FAFAFA' : '#222222'} size={20} />
              ) : (
                <Bell className="text-text-main-lv1" size={20} />
              )}
              <p className="text-[14px] font-medium leading-[17px] text-text-main-lv1">
                {isBettieChatAlarm ? t('BettieChatRoom.offAlarm') : t('BettieChatRoom.onAlarm')}
              </p>
            </button>
            <button
              type="button"
              onClick={handleLeaveChatRoom}
              className="flex items-center px-[24px] py-[16px] gap-[12px]"
            >
              <Exit fill={colorMode === 'dark' ? '#FAFAFA' : '#222222'} size={20} />
              <p className="text-[14px] font-medium leading-[17px] text-text-main-lv1">
                {t('BettieChatRoom.exitChatRoom')}
              </p>
            </button>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default BettieChatRoomSettingBottomDrawer;
